/**
 * Poly Component Library - Client-Side Hydration Entry Point
 *
 * Provides client-side hydration for server-rendered Poly components.
 * Uses Preact for smaller bundle size while maintaining React compatibility.
 *
 * HYDRATION APPROACH: Two-phase hydrate-then-render
 * - Phase One: Preact hydrate() attempts in-place attachment (caught if it throws)
 * - Decision: irreconcilable if hydrate threw OR produced childList mutations
 *   - Irreconcilable → unmount Preact vdom, clear container
 *   - Reconcilable → proceed as-is (attribute-only mutations handled by Phase Two)
 * - Phase Two: Preact render() — always runs (patches in place or builds from scratch)
 * - Blocks without server-rendered children skip Phase One and go straight to render()
 *
 * ERROR RECOVERY: Clone-based DOM restoration
 * - Server DOM is snapshotted before Phase One via cloneNode(true)
 * - Phase One failure sets irreconcilable flag; Phase Two still runs
 * - Phase Two failure → unmount Preact, restore snapshot (visually intact, no interactivity)
 * - Error fallback UI only shown when no recoverable content exists (empty containers)
 * - In production, unrecoverable errors produce an empty block (no error banner)
 *
 * TELEMETRY: Per-block, per-phase mutation counts emitted to CloudWatch RUM
 * - poly_hydration_mismatch — when mutations are detected
 * - poly_hydration_success — on completion (path: reconciled | clean-render | client-only)
 * - poly_hydration_error — on error-level log messages
 * - Optional onMismatch callback receives structured MismatchRecord
 *
 * BLOCK DATA: Block-level data attributes
 * - Component type stored in data-poly-component-type attribute
 * - Props stored as JSON string in data-poly-content attribute
 * - Each block is self-contained with its own data
 */

import { render, hydrate, createElement, ComponentType } from 'preact';
import { ComponentRegistry } from '../componentRegistry';
import type { MismatchRecord, HydrationConfig } from './types';
import { summarizeMutations, attachObserver } from './observer';
import { renderErrorFallback, restoreSnapshot } from './fallback';
import { emitMismatchToRum, emitSuccessToRum } from './rum-telemetry';
import { log } from './logger';

// Re-export types and utilities for external consumers and tests
export { summarizeMutations, attachObserver } from './observer';
export { renderErrorFallback, restoreSnapshot } from './fallback';
export { emitMismatchToRum, emitSuccessToRum } from './rum-telemetry';
export { log } from './logger';
export type { MutationSummary, MismatchRecord, HydrationConfig, ObserverHandle } from './types';

/**
 * Resolves the block identifier from a container element.
 * Prefers `data-poly-entity-id` (current convention), falls back to
 * `data-poly-block-id` (legacy renders), then `'unknown'`.
 */
export function getBlockId(block: Element): string {
  return block.getAttribute('data-poly-entity-id')
    || block.getAttribute('data-poly-block-id')
    || 'unknown';
}

/**
 * Component types PolyR wraps as blocks but hydration must never take over.
 *
 * `CookieConsentBanner` is stateless markup driven entirely by `poly-consent-manager-v1.js`,
 * which binds its control ids and reveals it. On irreconcilable markup `twoPhaseHydrate`
 * empties and re-renders the block, which re-hides the banner and strands the consent
 * manager's id-bound listeners on detached nodes, so the block is left exactly as served.
 * Behaviour for a type listed here must live in its own runtime: JSX handlers or state added
 * to it never attach and will silently do nothing.
 */
const NON_HYDRATED_COMPONENT_TYPES: ReadonlySet<string> = new Set(['CookieConsentBanner']);

export function hydrateComponents() {
  // Read optional hydration config (telemetry callback, etc.)
  const config: HydrationConfig = window.__POLY_HYDRATION_CONFIG__ || {};

  log('info', 'Starting hydration...');

  // Find all components (documents and blocks). We will only be processing blocks.
  const blocks = document.querySelectorAll('[data-poly-component-type]');

  blocks.forEach((block, index) => {
    const entityId = block.getAttribute('data-poly-entity-id') || block.getAttribute('data-poly-block-id');
    const entityType = block.getAttribute('data-poly-entity-type');
    const componentType = block.getAttribute('data-poly-component-type');
    const propsJson = block.getAttribute('data-poly-content');

    // Skip Document-level entities - only hydrate Block-level components
    // Documents are layout containers that hold blocks, they should not be hydrated
    if (entityType === 'Document') {
      log('debug', `Skipping Document entity: ${componentType}`);
      return;
    }

    if (!componentType) {
      log('error', 'Block missing data-poly-component-type');
      return;
    }

    if (NON_HYDRATED_COMPONENT_TYPES.has(componentType)) {
      log('debug', `Skipping non-hydrated ${componentType}`);
      return;
    }

    let props: Record<string, any> = {};
    if (propsJson) {
      try {
        props = JSON.parse(propsJson);
      } catch (error) {
        log('error', `Failed to parse props for ${componentType}:`, error);
        return;
      }
    }

    log('debug', `Block ${index + 1}:`, {
      blockId: entityId,
      componentType,
      props,
      hasChildren: block.children.length > 0
    });

    const Component = ComponentRegistry[componentType as keyof typeof ComponentRegistry];

    if (!Component) {
      log('error', `Component "${componentType}" not found in registry`);
      log('debug', 'Available components:', Object.keys(ComponentRegistry));
      return;
    }

    // Cast to Preact ComponentType for createElement compatibility
    const PreactComponent = Component as ComponentType<typeof props>;

    // Delegate per-block processing to processBlock()
    processBlock(block, PreactComponent, props, config);
  });
}


/**
 * Processes a single block container: determines the hydration path and handles errors.
 *
 * - Blocks with server-rendered children → two-phase hydrate-then-render via twoPhaseHydrate()
 * - Empty blocks → single render() call (client-only path)
 * - On success, stamps `data-poly-hydrated="true"` and emits success telemetry
 * - On error, renders error fallback (DEBUG) or empties block (prod)
 * - Invokes config.onMismatch callback when mutations are detected
 *
 * Returns the MismatchRecord if one was produced (two-phase path with mutations),
 * or null otherwise.
 */
export function processBlock(
  block: Element,
  component: ComponentType<any>,
  props: Record<string, any>,
  config: HydrationConfig
): MismatchRecord | null {
  const componentType = block.getAttribute('data-poly-component-type') || 'unknown';

  // TODO: emit logs and metrics to external store
  try {
    const hasExistingContent = block.children.length > 0;
    let mismatchRecord: MismatchRecord | null = null;

    if (hasExistingContent) {
      // Two-phase: hydrate() then render() with mismatch observation
      mismatchRecord = twoPhaseHydrate(block, component, props);

      if (mismatchRecord) {
        log('info', `✅ Hydrated ${componentType} (two-phase, ${mismatchRecord.phaseOne.totalMutations + mismatchRecord.phaseTwo.totalMutations} mutations)`);
        log('debug', 'Mismatch details:', mismatchRecord);
      } else {
        log('info', `✅ Hydrated ${componentType} (two-phase, no mismatches)`);
      }

      // Emit to CloudWatch RUM if available (best-effort, no-op if RUM hasn't loaded)
      if (mismatchRecord) {
        emitMismatchToRum(mismatchRecord);
      }

      // Invoke user-configured telemetry callback if present
      if (mismatchRecord && config.onMismatch) {
        try {
          config.onMismatch(mismatchRecord);
        } catch (telemetryError) {
          log('warn', 'Telemetry callback error:', telemetryError);
        }
      }
    } else {
      // Single render: no server content to hydrate
      render(createElement(component, props), block);
      log('info', `✅ Rendered ${componentType} (client-side)`);
    }

    // Stamp success
    block.setAttribute('data-poly-hydrated', 'true');

    // Emit success to RUM — determine which path was taken
    const blockIdForRum = getBlockId(block);
    if (!hasExistingContent) {
      // Client-only: empty container, single render() (no server content to hydrate)
      emitSuccessToRum(blockIdForRum, componentType, 'client-only');
    } else if (mismatchRecord) {
      // Clean render: irreconcilable mismatches detected, Phase Two built from scratch
      emitSuccessToRum(blockIdForRum, componentType, 'clean-render');
    } else {
      // Reconciled: Phase One hydration succeeded with zero mutations (ideal fast path)
      emitSuccessToRum(blockIdForRum, componentType, 'reconciled');
    }

    return mismatchRecord;
  } catch (error) {
    log('error', `Failed to render ${componentType}:`, error);
    renderErrorFallback(block);
    return null;
  }
}


/**
 * Executes two-phase hydration with mismatch observation on a single block.
 *
 * Phase One — Hydrate:
 * 1. Snapshot the server DOM via cloneNode(true) (kept intact for error recovery)
 * 2. Attach MutationObserver to capture all DOM changes
 * 3. Call hydrate() to attempt in-place attachment (caught if it throws)
 * 4. Collect Phase One mutation records
 *
 * Decision:
 * - Irreconcilable if hydrate threw OR produced childList mutations
 * - Irreconcilable → unmount Preact vdom, clear container for clean render
 * - Reconcilable → proceed as-is (attribute-only mutations expected)
 *
 * Phase Two — Render (always runs):
 * 5. Reconcilable path: render() diffs against hydrated DOM — minimal work
 * 6. Irreconcilable path: render() builds from scratch into empty container
 * 7. If render() throws: unmount Preact, restore snapshot from step 1
 * 8. Collect Phase Two mutations, build MismatchRecord, stamp data attributes
 *
 * Handles the service boundary between PolyRenderer (server) and PolyV (client)
 * where different dependency versions produce structurally divergent HTML.
 */
export function twoPhaseHydrate(
  block: Element,
  component: ComponentType<any>,
  props: Record<string, any>
): MismatchRecord | null {
  const blockId = getBlockId(block);
  const componentType = block.getAttribute('data-poly-component-type') || 'unknown';

  // Snapshot server-rendered DOM before Phase One for recovery
  const snapshot = block.cloneNode(true);

  // Attach observer before Phase One to capture all mutations
  const handle = attachObserver(block);

  // --- Phase One: attempt hydrate() ---
  let phaseOneRecords: MutationRecord[] = [];
  let irreconcilable = false;

  try {
    hydrate(createElement(component, props), block);
    phaseOneRecords = handle ? handle.takeRecords() : [];
  } catch (phaseOneError) {
    // Hydrate threw — DOM is in unknown state; set irreconcilable so Phase Two builds from scratch
    phaseOneRecords = handle ? handle.takeRecords() : [];
    irreconcilable = true;
    log('error', `Phase_One hydrate() failed for ${componentType}:`, phaseOneError);
  }

  // Decision: childList mutations = structural mismatch → irreconcilable
  const phaseOneSummary = summarizeMutations(phaseOneRecords);
  if (!irreconcilable && phaseOneSummary.childListChanges > 0) {
    irreconcilable = true;
  }

  // If irreconcilable, unmount Preact's vdom and clear the container
  // so Phase Two's render() builds from scratch instead of patching
  if (irreconcilable) {
    log('warn', `⚠️ Irreconcilable for ${componentType} (${phaseOneSummary.childListChanges} childList, ${phaseOneSummary.classChanges + phaseOneSummary.styleChanges + phaseOneSummary.otherAttributeChanges} attribute mutations) — clearing container for clean render`);
    try { render(null, block); } catch { /* best effort unmount */ }
    block.innerHTML = '';
  }

  // --- Phase Two: render() — always runs ---
  let phaseTwoRecords: MutationRecord[];
  try {
    render(createElement(component, props), block);
    phaseTwoRecords = handle ? handle.disconnect() : [];
  } catch (phaseTwoError) {
    if (handle) {
      try { handle.disconnect(); } catch (e) {
        log('error', 'Failed to disconnect MutationObserver:', e);
      }
    }
    // Unmount Preact vdom before restoring snapshot (may have state from Phase One or Phase Two)
    try { render(null, block); } catch { /* best effort */ }
    log('error', `Phase_Two render() failed for ${componentType}:`, phaseTwoError);
    restoreSnapshot(block, snapshot);
    return null;
  }

  const phaseTwoSummary = summarizeMutations(phaseTwoRecords);
  const totalMutations = phaseOneSummary.totalMutations + phaseTwoSummary.totalMutations;

  if (totalMutations > 0) {
    block.setAttribute('data-poly-hydration-mismatch', 'true');

    const record: MismatchRecord = {
      blockId,
      componentType,
      timestamp: Date.now(),
      phaseOne: phaseOneSummary,
      phaseTwo: phaseTwoSummary,
    };

    return record;
  }

  return null;
}


// Auto-hydrate on page load (with deduplication)
if (typeof window !== 'undefined') {
  // Prevent multiple hydrations if script is loaded multiple times
  if (!(window as any).__POLY_HYDRATED__) {
    (window as any).__POLY_HYDRATED__ = true;
    
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', hydrateComponents);
    } else {
      hydrateComponents();
    }
  }
}
